import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/TaskRulesPage.jsx
// Configurador visual de reglas automáticas de tareas.
import React, { useEffect, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem,
    Paper, Select, Stack, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, PlayArrow as RunIcon,
    History as HistoryIcon, ContentCopy as CopyIcon, AutoMode as AutoIcon, Save as SaveIcon,
    ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';

const OPERATORS = [
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'eq', label: '=' },
    { value: 'neq', label: '≠' },
    { value: 'between', label: i18n.t('hardcoded.pages_taskrulespage.entre') },
    { value: 'outside', label: i18n.t('hardcoded.pages_taskrulespage.fuera_de_rango') },
    { value: 'exists', label: i18n.t('hardcoded.pages_taskrulespage.existe_evento') },
];

const TRIGGERS = [
    { value: 'ticket_created', label: i18n.t('hardcoded.pages_taskrulespage.se_crea_un_ticket') },
    { value: 'telemetry_threshold', label: i18n.t('hardcoded.pages_taskrulespage.telemetria_cruza_umbral') },
    { value: 'no_report', label: i18n.t('hardcoded.pages_taskrulespage.falta_de_reporte_sensorreceptor') },
    { value: 'dual_yoyo', label: i18n.t('hardcoded.pages_taskrulespage.alarma_dual') },
];

const VARIABLES = [
    { value: 'PSI', label: i18n.t('hardcoded.pages_taskrulespage.presion_psi') },
    { value: 'TEMP', label: i18n.t('hardcoded.pages_taskrulespage.temperatura_c') },
    { value: 'ANY', label: i18n.t('hardcoded.pages_taskrulespage.cualquiera') },
];

const PRIORITIES = [
    { value: 'low', label: i18n.t('hardcoded.pages_taskrulespage.baja') }, { value: 'medium', label: i18n.t('hardcoded.pages_taskrulespage.media') },
    { value: 'high', label: i18n.t('hardcoded.pages_alarmticketpage.alta') }, { value: 'critical', label: i18n.t('alarmTickets.severity.critical') },
];

const DEDUP_MODES = [
    { value: 'while_open', label: i18n.t('hardcoded.pages_taskrulespage.mientras_haya_tarea_abierta') },
    { value: 'hours', label: i18n.t('hardcoded.pages_taskrulespage.por_x_horas') },
    { value: 'days', label: i18n.t('hardcoded.pages_taskrulespage.por_x_dias') },
    { value: 'never', label: i18n.t('hardcoded.pages_taskrulespage.no_deduplicar') },
];

const DUE_UNITS = [
    { value: 'minutes', label: i18n.t('hardcoded.pages_taskrulespage.minutos') }, { value: 'hours', label: i18n.t('hardcoded.components_alarmprofiles_profiledetail.horas') }, { value: 'days', label: i18n.t('hardcoded.components_alarmprofiles_profiledetail.dias') },
];

const blankAction = () => ({
    title_template: '',
    description_template: '',
    category: 'general',
    priority: 'medium',
    assigned_to: '',
    watchers: [],
    due_offset_value: '',
    due_offset_unit: 'hours',
});

const flattenVehicles = (vehicles = []) => {
    const out = [];
    const walk = (list) => {
        list.forEach((v) => {
            out.push(v);
            if (v.child_vehicles?.length) walk(v.child_vehicles);
        });
    };
    walk(vehicles);
    return out;
};

const userLabel = (u) => {
    if (!u) return '';
    const full = `${u.name || ''} ${u.last_name || ''}`.trim() || u.full_name || u.email;
    return `${full} · ${u.email}`;
};

const RuleEditor = ({ open, onClose, onSaved, profiles = [], users = [], vehicles = [], initial = null }) => {
  const { t } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const [saving, setSaving] = useState(false);
    const [rule, setRule] = useState(() => initial || {
        name: '', description: '', active: true,
        profile_id: '', scope_mode: 'all_profile_vehicles', vehicle_ids: [],
        condition: { trigger: 'ticket_created', variable: 'ANY', operator: 'exists', value: '', duration_minutes: '', repetitions: '' },
        dedup_mode: 'while_open', dedup_window_value: '', dedup_window_unit: 'hours',
        reopen_on_recurrence: false, evaluation_window_seconds: 0,
        actions: [blankAction()],
    });

    useEffect(() => {
        if (open) {
            setRule(initial || {
                name: '', description: '', active: true,
                profile_id: '', scope_mode: 'all_profile_vehicles', vehicle_ids: [],
                condition: { trigger: 'ticket_created', variable: 'ANY', operator: 'exists', value: '' },
                dedup_mode: 'while_open', dedup_window_value: '', dedup_window_unit: 'hours',
                reopen_on_recurrence: false, evaluation_window_seconds: 0,
                actions: [blankAction()],
            });
        }
    }, [open, initial]);

    const setField = (k, v) => setRule((p) => ({ ...p, [k]: v }));
    const setCond = (k, v) => setRule((p) => ({ ...p, condition: { ...p.condition, [k]: v } }));
    const updateAction = (idx, k, v) => setRule((p) => ({ ...p, actions: p.actions.map((a, i) => i === idx ? { ...a, [k]: v } : a) }));
    const addAction = () => setRule((p) => ({ ...p, actions: [...p.actions, blankAction()] }));
    const removeAction = (idx) => setRule((p) => ({ ...p, actions: p.actions.filter((_, i) => i !== idx) }));

    const preview = (() => {
        const trig = TRIGGERS.find((t) => t.value === rule.condition.trigger)?.label || rule.condition.trigger;
        const variable = VARIABLES.find((v) => v.value === rule.condition.variable)?.label || rule.condition.variable;
        const op = OPERATORS.find((o) => o.value === rule.condition.operator)?.label || rule.condition.operator;
        const value = rule.condition.value || '—';
        const dur = rule.condition.duration_minutes ? ` ${t('hardcoded.pages_taskrulespage.durante')} ${rule.condition.duration_minutes} ${t('hardcoded.pages_taskrulespage.min')}` : '';
        const profile = profiles.find((p) => p.id === rule.profile_id)?.name || t('hardcoded.pages_taskrulespage.cualquier_perfil');
        const tasks = rule.actions.map((a) => `“${a.title_template || t('hardcoded.pages_taskrulespage.sin_titulo')}”`).join(', ') || t('hardcoded.pages_taskrulespage.sin_tareas');
        return t('hardcoded.pages_taskrulespage.cuando_trigtolowercase_con_variabletolower_op', { trigtoLowerCase: trig.toLowerCase(), variabletoLower: variable.toLowerCase(), op: op, value: value, dur: dur, profile: profile, tasks: tasks });
    })();

    const save = async () => {
        if (!rule.name.trim()) { showError(t('hardcoded.pages_chatpage.nombre_requerido')); return; }
        if (!rule.actions?.length) { showError(t('hardcoded.pages_taskrulespage.al_menos_una_tarea_es')); return; }
        for (const a of rule.actions) {
            if (!a.title_template?.trim()) { showError(t('hardcoded.pages_taskrulespage.cada_tarea_necesita_un_titulo')); return; }
        }
        setSaving(true);
        try {
            const payload = {
                ...rule,
                profile_id: rule.profile_id || null,
                vehicle_ids: rule.vehicle_ids || [],
                condition: {
                    ...rule.condition,
                    value: rule.condition.value === '' ? null : (isNaN(Number(rule.condition.value)) ? rule.condition.value : Number(rule.condition.value)),
                    duration_minutes: rule.condition.duration_minutes ? Number(rule.condition.duration_minutes) : null,
                    repetitions: rule.condition.repetitions ? Number(rule.condition.repetitions) : null,
                },
                dedup_window_value: rule.dedup_window_value ? Number(rule.dedup_window_value) : null,
                actions: rule.actions.map((a, i) => ({
                    ...a,
                    sort_order: i,
                    assigned_to: a.assigned_to || null,
                    due_offset_value: a.due_offset_value === '' ? null : Number(a.due_offset_value),
                    watchers: a.watchers || [],
                })),
            };
            if (initial?.id) {
                await api.taskRules.update(initial.id, payload);
                showSuccess(t('hardcoded.pages_taskrulespage.regla_actualizada'));
            } else {
                await api.taskRules.create(payload);
                showSuccess(t('hardcoded.pages_taskrulespage.regla_creada'));
            }
            onSaved?.();
            onClose();
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.pages_taskrulespage.error_guardando_la_regla'));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>{initial ? t('hardcoded.pages_taskrulespage.editar_regla') : t('hardcoded.pages_taskrulespage.nueva_regla_automatica')}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <TextField fullWidth label={t('hardcoded.pages_taskrulespage.nombre_de_la_regla')} value={rule.name} onChange={(e) => setField('name', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                            <Switch checked={rule.active} onChange={(e) => setField('active', e.target.checked)} />
                            <Typography variant="body2">{rule.active ? t('hardcoded.components_ai_aimemoryeditor.activa') : t('hardcoded.components_ai_aimemoryeditor.inactiva')}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label={t('audit.table.description')} value={rule.description} onChange={(e) => setField('description', e.target.value)} multiline rows={2} />
                    </Grid>

                    <Grid item xs={12}><Divider>{i18n.t('hardcoded.components_ai_aimemoryeditor.alcance')}</Divider></Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel shrink>{t('hardcoded.pages_taskrulespage.perfil_tpms')}</InputLabel>
                            <Select value={rule.profile_id || ''} displayEmpty notched label={t('hardcoded.pages_taskrulespage.perfil_tpms')}
                                onChange={(e) => setField('profile_id', e.target.value)}
                                renderValue={(v) => profiles.find((p) => p.id === v)?.name || t('hardcoded.pages_taskrulespage.cualquier_perfil')}>
                                <MenuItem value="">{t('hardcoded.pages_taskrulespage.cualquier_perfil')}</MenuItem>
                                {profiles.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_taskrulespage.alcance_de_vehiculos')}</InputLabel>
                            <Select value={rule.scope_mode} label={t('hardcoded.pages_taskrulespage.alcance_de_vehiculos')} onChange={(e) => setField('scope_mode', e.target.value)}>
                                <MenuItem value="all_profile_vehicles">{t('hardcoded.pages_taskrulespage.todos_los_vehiculos_del_perfil')}</MenuItem>
                                <MenuItem value="all_company_vehicles">{t('hardcoded.pages_taskrulespage.todos_los_vehiculos_de_la')}</MenuItem>
                                <MenuItem value="specific_vehicles">{t('hardcoded.pages_taskrulespage.vehiculos_especificos')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {rule.scope_mode === 'specific_vehicles' && (
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={vehicles}
                                getOptionLabel={(o) => o?.display_name || o?.alias || o?.id || ''}
                                value={vehicles.filter((v) => (rule.vehicle_ids || []).includes(v.id))}
                                onChange={(_, val) => setField('vehicle_ids', val.map((v) => v.id))}
                                renderInput={(params) => <TextField {...params} label={t('hardcoded.pages_taskrulespage.vehiculos_especificos')} placeholder={t('hardcoded.pages_taskrulespage.selecciona_uno_o_varios')} />}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}><Divider>{t('hardcoded.pages_taskrulespage.condicion')}</Divider></Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>{t('pages_taskrulespage.field_trigger')}</InputLabel>
                            <Select value={rule.condition.trigger} label={t('pages_taskrulespage.field_trigger')} onChange={(e) => setCond('trigger', e.target.value)}>
                                {TRIGGERS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>{i18n.t('hardcoded.pages_taskrulespage.variable')}</InputLabel>
                            <Select value={rule.condition.variable || 'ANY'} label={t('hardcoded.pages_taskrulespage.variable')} onChange={(e) => setCond('variable', e.target.value)}>
                                {VARIABLES.map((v) => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_operators_vehicleoperatorspanel.operador')}</InputLabel>
                            <Select value={rule.condition.operator} label={t('hardcoded.components_operators_vehicleoperatorspanel.operador')} onChange={(e) => setCond('operator', e.target.value)}>
                                {OPERATORS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth label={t('hardcoded.pages_taskrulespage.valor_o_minmax_para_entrefuera')} value={rule.condition.value ?? ''} onChange={(e) => setCond('value', e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField fullWidth type="number" label={t('hardcoded.pages_taskrulespage.duracion_min')} value={rule.condition.duration_minutes ?? ''} onChange={(e) => setCond('duration_minutes', e.target.value)} />
                    </Grid>

                    <Grid item xs={12}><Divider>{t('hardcoded.pages_taskrulespage.deduplicacion')}</Divider></Grid>
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth>
                            <InputLabel>{t('pages_taskrulespage.field_mode')}</InputLabel>
                            <Select value={rule.dedup_mode} label={t('pages_taskrulespage.field_mode')} onChange={(e) => setField('dedup_mode', e.target.value)}>
                                {DEDUP_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    {(rule.dedup_mode === 'hours' || rule.dedup_mode === 'days') && (
                        <>
                            <Grid item xs={6} md={3}>
                                <TextField fullWidth type="number" label={t('hardcoded.pages_taskrulespage.ventana')} value={rule.dedup_window_value ?? ''} onChange={(e) => setField('dedup_window_value', e.target.value)} />
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('hardcoded.components_alarmprofiles_profileform.unidad')}</InputLabel>
                                    <Select value={rule.dedup_window_unit || 'hours'} label={t('hardcoded.components_alarmprofiles_profileform.unidad')} onChange={(e) => setField('dedup_window_unit', e.target.value)}>
                                        <MenuItem value="hours">{i18n.t('hardcoded.components_alarmprofiles_profiledetail.horas')}</MenuItem>
                                        <MenuItem value="days">{t('hardcoded.components_alarmprofiles_profiledetail.dias')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}><Divider>{t('hardcoded.pages_taskrulespage.tareas_a_crear')}</Divider></Grid>
                    {rule.actions.map((a, i) => (
                        <Grid item xs={12} key={i}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={700}>{i18n.t('hardcoded.pages_taskrulespage.tarea')}{i + 1}</Typography>
                                    {rule.actions.length > 1 && (
                                        <IconButton size="small" onClick={() => removeAction(i)}><DeleteIcon fontSize="small" /></IconButton>
                                    )}
                                </Stack>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={8}>
                                        <TextField fullWidth size="small" label={t('hardcoded.pages_taskrulespage.titulo_de_la_tarea')} value={a.title_template}
                                            onChange={(e) => updateAction(i, 'title_template', e.target.value)}
                                            helperText={t('hardcoded.pages_taskrulespage.usa_llaves_para_personalizar')} />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('hardcoded.components_tickets_ticketactioncenter.prioridad')}</InputLabel>
                                            <Select value={a.priority} label={t('hardcoded.components_tickets_ticketactioncenter.prioridad')} onChange={(e) => updateAction(i, 'priority', e.target.value)}>
                                                {PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <TextField fullWidth size="small" label={t('hardcoded.components_admin_categoryform.categoria')} value={a.category}
                                            onChange={(e) => updateAction(i, 'category', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth size="small" label={t('hardcoded.pages_chatpage.descripcion_opcional')} multiline rows={2}
                                            value={a.description_template} onChange={(e) => updateAction(i, 'description_template', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Autocomplete
                                            size="small"
                                            options={users}
                                            getOptionLabel={userLabel}
                                            value={users.find((u) => u.id === a.assigned_to) || null}
                                            onChange={(_, val) => updateAction(i, 'assigned_to', val?.id || '')}
                                            renderInput={(params) => <TextField {...params} label={t('hardcoded.pages_taskrulespage.asignar_a_opcional')} placeholder={t('hardcoded.components_settings_companynetworkstab.buscar_usuario')} />}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField fullWidth size="small" type="number" label={t('hardcoded.pages_taskrulespage.vencimiento_en')} value={a.due_offset_value}
                                            onChange={(e) => updateAction(i, 'due_offset_value', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('hardcoded.components_alarmprofiles_profileform.unidad')}</InputLabel>
                                            <Select value={a.due_offset_unit} label={t('hardcoded.components_alarmprofiles_profileform.unidad')} onChange={(e) => updateAction(i, 'due_offset_unit', e.target.value)}>
                                                {DUE_UNITS.map((u) => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Button startIcon={<AddIcon />} onClick={addAction}>{i18n.t('hardcoded.pages_taskrulespage.agregar_tarea')}</Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2"><strong>{i18n.t('hardcoded.components_admin_couponform.vista_previa')}</strong> {preview}</Typography>
                        </Alert>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}>
                    {initial ? t('settings.company.saveChanges') : t('hardcoded.pages_taskrulespage.crear_regla')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TaskRulesPage = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const { showError, showSuccess } = useNotification();
    const [rules, setRules] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [users, setUsers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState({ open: false, initial: null });

    const load = async () => {
        setLoading(true);
        try {
            const [rRules, rProfiles, rUsers, rVehicles] = await Promise.all([
                api.taskRules.list(false),
                api.alarmProfiles.getAll({ active_only: true }),
                api.users.getAll().catch(() => ({ data: [] })),
                api.vehicles.getAll().catch(() => ({ data: [] })),
            ]);
            setRules(rRules.data || []);
            setProfiles(rProfiles.data || []);
            setUsers(rUsers.data || []);
            setVehicles(flattenVehicles(rVehicles.data || []));
        } catch (e) {
            showError(t('hardcoded.pages_taskrulespage.error_cargando_reglas'));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggle = async (id) => {
        try {
            await api.taskRules.toggle(id);
            load();
        } catch (e) { showError(t('hardcoded.pages_taskrulespage.error_alternar_regla')); }
    };

    const remove = async (rule) => {
        if (!window.confirm(t('hardcoded.pages_taskrulespage.eliminar_la_regla_name', { name: rule.name }))) return;
        try {
            await api.taskRules.delete(rule.id);
            showSuccess(t('hardcoded.pages_taskrulespage.regla_eliminada'));
            load();
        } catch (e) { showError(t('hardcoded.pages_taskrulespage.error_eliminar_regla')); }
    };

    return (
        <Box sx={{ pb: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => navigate('/tasks')}><ArrowBack /></IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="primary.main">{t('hardcoded.pages_taskrulespage.reglas_automaticas_de_tareas')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('hardcoded.pages_taskrulespage.crea_tareas_automaticas_cuando_se')}</Typography>
                    </Box>
                </Stack>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditor({ open: true, initial: null })}>{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.nueva_regla')}</Button>
            </Stack>

            {loading && <CircularProgress />}

            <Grid container spacing={2}>
                {rules.map((r) => (
                    <Grid item xs={12} md={6} key={r.id}>
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <AutoIcon color={r.active ? 'success' : 'disabled'} />
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>{r.name}</Typography>
                                    <Switch checked={r.active} onChange={() => toggle(r.id)} size="small" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{r.description || t('hardcoded.pages_billingplanspage.sin_descripcion')}</Typography>
                                <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                                    {r.profile_name && <Chip size="small" label={t('hardcoded.pages_taskrulespage.perfil_profile_name', { profile_name: r.profile_name })} />}
                                    <Chip size="small" label={t('hardcoded.pages_taskrulespage.trigger_label', { trigger: r.condition?.trigger || '—' })} />
                                    {r.condition?.variable && <Chip size="small" label={`${r.condition.variable} ${r.condition.operator || ''} ${r.condition.value ?? ''}`} />}
                                    <Chip size="small" color="primary" label={t('hardcoded.pages_taskrulespage.ractionslength0_tareas', { ractionslength0: r.actions?.length || 0 })} />
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {i18n.t('hardcoded.pages_taskrulespage.dedup')}{r.dedup_mode}{r.dedup_window_value ? ` (${r.dedup_window_value} ${r.dedup_window_unit})` : ''}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => setEditor({ open: true, initial: r })}>{i18n.t('common.edit')}</Button>
                                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(r)}>{t('audit.actions.DELETE')}</Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {!rules.length && !loading && (
                    <Grid item xs={12}>
                        <Alert severity="info">{t('hardcoded.pages_taskrulespage.aun_no_hay_reglas_configuradas')}</Alert>
                    </Grid>
                )}
            </Grid>

            <RuleEditor open={editor.open} onClose={() => setEditor({ open: false, initial: null })}
                onSaved={load} profiles={profiles} users={users} vehicles={vehicles} initial={editor.initial} />
        </Box>
    );
};

export default TaskRulesPage;
